import Drawer from "components/drawerSlide/drawer";
import FormLayout from "components/form/FormLayout";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./eventsCalendarDrawer.scss";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Input from "components/form/input";
import Checkbox from "components/form/checkbox/Checkbox";
import { Select } from "components/select/index";
import classNames from "classnames";
import moment from "moment";
import TimePick from "components/timePick/TimePick";
//import TextEditor from "components/textEditor/textEditor";
import TextField from "components/form/textField/TextField";
import { isEqual } from "lodash";
import MultipleActionDropdown from "components/multipleActionDropdown/MultipleActionDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import ButtonGroup from "components/buttonGroup/ButtonGroup";
import axios, { CancelToken } from "axios";
import { DeleteEventApplicationTeeSheet, DeleteEventApplicationTemplate, GetEventApplication, TEventApplication } from "api/rpc/2024-04/facilityAdmin/event/eventApplication";
import { StatusCode } from "api/protocols";
import ReactDOM from "react-dom";
import EventApplicationNotification from "./EventApplicationNotification";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import Spin from "components/spin/spin";

export type TEventDrawerMode = "create" | "edit";
interface IProps {
  open: boolean;
  mode?: TEventDrawerMode;
  onClose: () => void;
  onOk: (settings: IEventSettings) => Promise<boolean>;
  onApplicationEditOpen: (application: TEventApplication, key: "template" | "tee-sheet", eventId: number, eventType: "league" | "tournament", tournamentType: "regular" | "shotgun" | "crossover") => void;
  presetSettings?: IEventSettings;
  editActions?: IEventDrawerEditActions;
  handleOpenScheduler?: (key: string | number, eventId: number, eventType: "league" | "tournament", tournamentType: "regular" | "shotgun" | "crossover") => void;
  applicationRefresh: number;
}

export interface IEventDrawerEditActions {
  onViewMoreDetails: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

export interface IEventSettings {
  id?: number;
  name: string;
  type: "golf" | "hospitality";
  category: "league" | "tournament" | "wedding" | "banquet" | "other";
  start_date: Date;
  end_date: Date;
  start_time: string;
  week_day: string;
  tournament_type: "regular" | "shotgun" | "crossover";
  description: string;
}

export default function NewEventDrawer(props: IProps) {
  const {
    open,
    mode = "create",
    onClose,
    onOk,
    onApplicationEditOpen,
    presetSettings,
    editActions,
    handleOpenScheduler,
    applicationRefresh,
  } = props;
  const isEdit = mode === "edit";
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Option } = Select;
  const [state, setState] = useState<IEventSettings>({
    name: "",
    type: "golf",
    category: "league",
    start_date: new Date(),
    end_date: new Date(),
    start_time: "12:00",
    week_day: "Monday",
    tournament_type: "regular",
    description: "",
  });

  const { state: deleteModal, updateModal: updateDeleteModal, closeModal: closeDeleteModal } = useModal<{ id: number, templateId: number }>({ id: null, templateId: null });

  const [eventApplications, setEventApplications] = useState<Array<TEventApplication>>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (open) {
      // Preset the settings if set
      if (!isEqual(state, presetSettings)) {
        setState({ ...presetSettings });
      }

      if (isEdit && presetSettings && (presetSettings?.category === "league" || presetSettings?.category === "tournament")) {
        void getEventApplications(source.token);
      }
    }
    return () => source.cancel();
  }, [open, presetSettings, applicationRefresh]);

  async function getEventApplications(cancelToken?: CancelToken) {
    if (eventApplications !== undefined) {
      setEventApplications(undefined); // loader
    }
    const res = await GetEventApplication({ event_id: presetSettings?.id, event_type: presetSettings?.category === "league" ? "league" : "tournament" }, false, cancelToken);
    if (res?.status === StatusCode.OK) {
      setEventApplications(res?.data ?? []);
    } else {
      setEventApplications([]);
    }
  }

  const GOLF_CATEGORIES = [
    { label: t("League"), value: "league" },
    { label: t("Tournament"), value: "tournament" },
  ];

  const HOSPITALITY_CATEGORIES = [
    { label: t("Wedding"), value: "wedding" },
    { label: t("Banquet"), value: "banquet" },
    { label: t("Other"), value: "other" },
  ];

  function handleClose() {
    // Reset drawer
    ReactDOM.unstable_batchedUpdates(() => {
      setState({
        name: "",
        type: "golf",
        category: "league",
        start_date: new Date(),
        end_date: new Date(),
        start_time: "12:00",
        week_day: "Monday",
        tournament_type: "regular",
        description: "",
      });
      setEventApplications([]);
    });

    void onClose();
  }

  async function handleOk() {
    const eventCreated = await onOk({ ...state });
    if (eventCreated) {
      void handleClose();
    }
  }

  async function handleDeleteApplication() {
    if (deleteModal?.templateId) {
      //Template
      const res = await DeleteEventApplicationTemplate({ id: deleteModal?.id }, true);
      if (res?.status !== StatusCode.OK) {
        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error deleting event application")));
        return;
      }
    } else {
      //Live tee sheet
      const res = await DeleteEventApplicationTeeSheet({ id: deleteModal?.id }, true);
      if (res?.status !== StatusCode.OK) {
        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error deleting event application")));
        return;
      }
    }
    dispatch(showSuccess(t("Successfully deleted event application")));
    void getEventApplications();
    void closeDeleteModal();

  }

  return (
    <Drawer
      title={isEdit ? t("Edit event") : t("New event")}
      subtitle={moment(state.start_date)?.format("ll")}
      onClose={handleClose}
      okText={isEdit ? t("Save") : t("Create event")}
      cancelText={t("Cancel")}
      showCancel
      onOk={handleOk}
      open={open}
      headerActions={
        isEdit ? (
          <div className="drawer-header-actions">
            <MultipleActionDropdown
              alignment="right"
              options={[
                {
                  type: "handler",
                  label: t("View more details"),
                  handler: editActions?.onViewMoreDetails,
                  icon: "arrow-up-right-from-square",
                },
                {
                  type: "handler",
                  label: t("Duplicate"),
                  handler: editActions?.onDuplicate,
                  icon: "layer-group",
                },
                {
                  type: "handler",
                  label: t("Delete"),
                  handler: editActions?.onDelete,
                  icon: "trash-can",
                  colour: "error",
                },
              ]}
              disabled={false}
            >
              <FontAwesomeIcon icon={faEllipsisVertical} className="cursor-pointer" />
            </MultipleActionDropdown>
          </div>
        ) : null
      }
    >
      <Drawer.Section bordered>
        <p className="ui-events-calendar-drawer-title">{t("Event details")}</p>
      </Drawer.Section>
      <Drawer.Section>
        <FormLayout>
          <FormLayout.Group>
            <Input
              label={t("Name")}
              value={state.name}
              onChange={e => setState(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t("Name")}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <div className="text-base font-medium text-black block">
              <label className="ui-teesheet-template-label">{t("Type")}</label>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="type"
                  size="medium"
                  checked={state.type === "golf"}
                  onChange={() => setState(prev => ({ ...prev, type: "golf", category: "league" }))}
                  label={t("Golf")}
                  selectionOutline
                  circle
                  disabled={isEdit}
                />
                <Checkbox
                  id="type"
                  size="medium"
                  checked={state.type === "hospitality"}
                  onChange={() => setState(prev => ({ ...prev, type: "hospitality", category: "wedding" }))}
                  label={t("Hospitality")}
                  selectionOutline
                  circle
                  disabled={isEdit}
                />
              </div>
            </div>
          </FormLayout.Group>
          <FormLayout.Group>
            <Select
              defaultValue={state.category}
              onChange={value => setState(prev => ({ ...prev, category: value }))}
              label={state.type === "golf" ? t("Golf event category") : t("Hospitality event category")}
              disabled={isEdit}
            >
              {(state.type === "golf" ? GOLF_CATEGORIES : HOSPITALITY_CATEGORIES)?.map((category, index) => {
                return (
                  <Option key={index} value={category.value}>
                    <div className="flex items-center gap-2">
                      <div
                        className={classNames("ui-events-calendar-drawer-colour", {
                          "ui-events-calendar-drawer-colour-disabled": isEdit,
                          [`ui-events-calendar-drawer-colour-${category.value}`]: category.value && !isEdit,
                        })}
                      />
                      {category.label}
                    </div>
                  </Option>
                );
              })}
            </Select>
          </FormLayout.Group>
        </FormLayout>
      </Drawer.Section>
      <Drawer.Section bordered>
        <p className="ui-events-calendar-drawer-title">{t("Schedule")}</p>
      </Drawer.Section>
      <Drawer.Section>
        <FormLayout>
          <FormLayout.Group>
            <DatePickerInput
              months={1}
              position="left"
              label={t("Start date")}
              startingDate={state.start_date}
              setStartingDate={date => setState(prev => ({ ...prev, start_date: date as Date }))}
            />
            <DatePickerInput
              months={1}
              position="right"
              label={t("End date")}
              startingDate={state.end_date}
              setStartingDate={date => setState(prev => ({ ...prev, end_date: date as Date }))}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <TimePick
              label={t("Start time")}
              value={state.start_time}
              size="large"
              onChange={time => setState(prev => ({ ...prev, start_time: time }))}
            />
            {state.category === "league" ? (
              <Select
                defaultValue={state.week_day}
                onChange={value => setState(prev => ({ ...prev, week_day: value }))}
                label={t("Week day")}
              >
                {moment?.weekdays()?.map((day, index) => {
                  return (
                    <Option key={index} value={day}>
                      {day}
                    </Option>
                  );
                })}
              </Select>
            ) : null}
            {/* state.category === "tournament" ? (
              <Select
                defaultValue={state.tournament_type}
                onChange={value => setState(prev => ({ ...prev, tournament_type: value }))}
                label={t("Tournament type")}
              >
                {["Regular", "Shotgun", "Crossover"]?.map((type, index) => {
                  return (
                    <Option key={index} value={type?.toLowerCase()}>
                      {type}
                    </Option>
                  );
                })}
              </Select> */}
          </FormLayout.Group>
          {state.category === "tournament" ? (
            <FormLayout.Group>
              {/* <TextEditor
                            markdownText={state.description}
                            markdownTextOnChange={text => setState(prev => ({...prev, description: text}))}
                            label={t("Description (optional)")}
                        /> */}
              <TextField
                label={t("Description (optional)")}
                value={state.description}
                onChange={e => setState(prev => ({ ...prev, description: e.target.value }))}
              />
            </FormLayout.Group>
          ) : null}
        </FormLayout>
      </Drawer.Section>

      {state.type === "golf" && isEdit ? (
        <div>
          <Drawer.Section bordered>
            <p className="ui-events-calendar-drawer-title">{t("Tee sheet")}</p>
          </Drawer.Section>
          <Drawer.Section>
            {eventApplications === undefined ? <div style={{ display: "flex", justifyContent: "center", width: "100%", height: "40px" }}>
              <Spin />
            </div> : eventApplications && eventApplications?.length > 0 ? (
              <div className="flex flex-column gap-2 mb-3">
                {eventApplications?.map((application, index) => {
                  return (
                    <EventApplicationNotification key={index} onEditOpen={(application, key) => onApplicationEditOpen(application, key, presetSettings?.id, presetSettings?.category === "league" ? "league" : "tournament", presetSettings?.tournament_type)} application={application} onDelete={id => updateDeleteModal({ id, isOpen: true, templateId: application?.template_id })} />
                  );
                })}
              </div>
            ) : null}
            <div>
              <ButtonGroup fullWidth noSelection onButtonClick={key => handleOpenScheduler(key, presetSettings?.id, presetSettings?.category === "league" ? "league" : "tournament", presetSettings?.tournament_type)} startKey={"none"} buttons={[{ label: t("Apply to tee sheet"), key: "tee-sheet" }, { label: t("Apply to template"), key: "template" }]} />
            </div>
          </Drawer.Section>
        </div>
      ) : null}


      <Portal
        isMounted={deleteModal?.isOpen}
      >
        <Popup open={deleteModal?.isOpen} title={t("Delete event application")} type="warning" description={t("Are you sure you want to delete this application?")} onCancel={closeDeleteModal} onOk={handleDeleteApplication} okText={t("Delete")} />
      </Portal>
    </Drawer>
  );
}
